import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Landmark, ArrowRight, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const DEFAULTS = {
  eyebrow: "Ressources et patrimoine",
  title: "Bibliothèques et Musées",
  banner_image: "/media/library-shelves.jpg",
  intro:
    "Cette structure regroupe les Bibliothèques (centrale et numérique) ainsi que les Musées universitaires de l'UNISIC. Elle assure la conservation, la valorisation et la diffusion du patrimoine documentaire et culturel de l'institution.",
};

export default function BibliothequesMuseesCentrale() {
  const [content, setContent] = useState(DEFAULTS);
  const [subOpen, setSubOpen] = useState(false);
  useEffect(() => {
    api.get("/content/bm.centrale").then((r) => setContent({ ...DEFAULTS, ...(r.data || {}) })).catch(() => {});
  }, []);
  return (
    <div data-testid="bm-centrale-page">
      <section className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img src={content.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{content.eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight max-w-4xl">{content.title}</h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="eyebrow eyebrow-dark">Présentation</div>
        <span className="divider-gold mt-3" />
        <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-4xl whitespace-pre-line">{content.intro}</p>

        {/* BLOC 1 : Bibliothèques */}
        <div className="mt-16 grid lg:grid-cols-3 gap-8" data-testid="bloc-bibliotheques">
          <div className="lg:col-span-2">
            <div className="eyebrow eyebrow-dark">Bloc 1</div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-unisic-blue">Bibliothèques</h2>
            <span className="divider-gold mt-3" />
            <p className="mt-4 text-slate-700">
              La Bibliothèque centrale et la Bibliothèque numérique accompagnent les étudiants et chercheurs dans leurs travaux, aux côtés des activités d'animation scientifique.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <Link to="/directions/bibliotheques-musees/bibliotheques-musees/bibliotheque-centrale" className="paper-card card-hover p-5 flex flex-col" data-testid="bib-card-centrale">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-unisic-blue text-white mb-3"><BookOpen className="w-5 h-5" /></div>
                <div className="text-[11px] uppercase tracking-widest text-unisic-gold font-semibold">Sur place</div>
                <h3 className="mt-1 font-semibold text-unisic-blue">Bibliothèque centrale</h3>
                <p className="mt-2 text-xs text-slate-600 flex-1">Collections physiques, salle de lecture, prêt.</p>
                <ArrowRight className="w-4 h-4 mt-3 text-unisic-blue" />
              </Link>
              <Link to="/directions/bibliotheques-musees/bibliotheques-musees/bibliotheque-numerique" className="paper-card card-hover p-5 flex flex-col" data-testid="bib-card-numerique">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-unisic-gold text-unisic-blue mb-3"><BookOpen className="w-5 h-5" /></div>
                <div className="text-[11px] uppercase tracking-widest text-unisic-gold font-semibold">En ligne</div>
                <h3 className="mt-1 font-semibold text-unisic-blue">Bibliothèque numérique</h3>
                <p className="mt-2 text-xs text-slate-600 flex-1">Ressources documentaires et scientifiques.</p>
                <ArrowRight className="w-4 h-4 mt-3 text-unisic-blue" />
              </Link>
              <Link to="/directions/bibliotheques-musees/bibliotheques-musees/activites-animation" className="paper-card card-hover p-5 flex flex-col" data-testid="bib-card-activites">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-unisic-red text-white mb-3"><Landmark className="w-5 h-5" /></div>
                <div className="text-[11px] uppercase tracking-widest text-unisic-gold font-semibold">Événements</div>
                <h3 className="mt-1 font-semibold text-unisic-blue">Activités d'animation scientifique</h3>
                <p className="mt-2 text-xs text-slate-600 flex-1">Colloques, ateliers, conférences.</p>
                <ArrowRight className="w-4 h-4 mt-3 text-unisic-blue" />
              </Link>
            </div>
          </div>

          {/* Espace d'abonnement (droite) */}
          <aside className="paper-card p-6 h-fit lg:sticky lg:top-24" data-testid="subscription-block">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-unisic-gold text-unisic-blue mb-3"><Send className="w-5 h-5" /></div>
            <div className="eyebrow eyebrow-dark">Abonnement / Inscription</div>
            <span className="divider-gold mt-2" />
            <p className="mt-4 text-sm text-slate-700 leading-relaxed">
              Accédez aux ressources numériques réservées, à la Bibliothèque numérique complète et aux notifications sur les nouveaux ouvrages.
            </p>
            <button onClick={() => setSubOpen(true)} className="mt-5 btn-primary text-sm w-full justify-center" data-testid="btn-open-subscription">
              Demander un abonnement
            </button>
            <p className="mt-3 text-[11px] text-slate-500">Réservé aux étudiants, enseignants et chercheurs de l'UNISIC.</p>
          </aside>
        </div>

        {/* BLOC 2 : Musées */}
        <div className="mt-20" data-testid="bloc-musees">
          <div className="eyebrow eyebrow-dark">Bloc 2</div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-unisic-blue">Musées universitaires</h2>
          <span className="divider-gold mt-3" />
          <p className="mt-4 text-slate-700 max-w-4xl">
            Les Musées universitaires de l'UNISIC valorisent le patrimoine médiatique, culturel et technologique de la République Démocratique du Congo. Cinq espaces thématiques présentent la richesse audiovisuelle, cinématographique, journalistique et numérique du pays.
          </p>
          <Link to="/directions/bibliotheques-musees/bibliotheques-musees/musees" className="mt-6 inline-flex btn-primary text-sm" data-testid="btn-explore-musees">
            <Landmark className="w-4 h-4" /> Explorer les 5 musées universitaires
          </Link>
        </div>
      </section>

      {subOpen && <SubModal onClose={() => setSubOpen(false)} />}
    </div>
  );
}

function SubModal({ onClose }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", affiliation: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await api.post("/subscription-requests", form); setDone(true); }
    catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-unisic-blue">Demande d'abonnement</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded">✕</button>
        </div>
        {done ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-700">Votre demande a été transmise. Le Service des Bibliothèques vous contactera prochainement.</p>
            <button onClick={onClose} className="mt-6 btn-secondary text-sm">Fermer</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="sub-modal-form">
            <input required placeholder="Nom complet" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
            <input required type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
            <input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
            <input placeholder="Affiliation (institution, faculté...)" value={form.affiliation} onChange={(e) => setForm({...form, affiliation: e.target.value})} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
            <textarea rows={3} placeholder="Motif de la demande" value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
            {error && <div className="text-xs text-unisic-red">{error}</div>}
            <button type="submit" disabled={busy} className="btn-primary text-sm w-full justify-center">
              <Send className="w-4 h-4" /> {busy ? "Envoi..." : "Envoyer"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
